import type { PageSchema } from '@shopexperts/shared';

export type SchemaSort = 'label' | 'pages' | 'updated';

export const SCHEMA_SORTS: SchemaSort[] = ['label', 'pages', 'updated'];

export function isSchemaSort(value: unknown): value is SchemaSort {
  return typeof value === 'string' && SCHEMA_SORTS.includes(value as SchemaSort);
}

/** Filters on everything shown in a row, so searching matches what people read. */
export function filterSchemas(schemas: PageSchema[], search: string): PageSchema[] {
  const query = search.trim().toLowerCase();
  if (!query) return schemas;

  return schemas.filter(schema => (
    schema.label.toLowerCase().includes(query)
    || schema.name.toLowerCase().includes(query)
    || (schema.description || '').toLowerCase().includes(query)
  ));
}

export function sortSchemas(schemas: PageSchema[], search: string, sort: SchemaSort): PageSchema[] {
  const filtered = [...filterSchemas(schemas, search)];

  if (sort === 'pages') {
    // Ties fall back to label so the order never depends on the API's ordering.
    return filtered.sort((a, b) => (b.pages_count ?? 0) - (a.pages_count ?? 0) || compareLabels(a, b));
  }

  if (sort === 'updated') {
    return filtered.sort((a, b) => timestamp(b.updated_at) - timestamp(a.updated_at) || compareLabels(a, b));
  }

  return filtered.sort(compareLabels);
}

function compareLabels(a: PageSchema, b: PageSchema): number {
  return a.label.localeCompare(b.label, 'en', { sensitivity: 'base' });
}

function timestamp(value: string | undefined): number {
  if (!value) return 0;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}
